import axios, { type AxiosInstance } from 'axios';
import { ACCESS_TOKEN_KEY, CURRENT_NETWORK_KEY } from '../auth/keycloak';

/**
 * Attaches the Viana auth headers to an axios instance.
 *
 * Both headers are read from localStorage on EVERY request rather than closed over, so a silent
 * Keycloak refresh is picked up without re-registering anything.
 *
 * This takes an instance because `axios.create()` does NOT inherit interceptors registered on the
 * global default instance — they are separate interceptor managers. Every instance that talks to
 * a Viana service has to be installed explicitly; forgetting one means requests go out
 * unauthenticated and come back 401.
 */
export function installVianaHeaders(instance: AxiosInstance): AxiosInstance {
  /*
   * A request that never answers is worse than one that fails.
   *
   * Several viana-services routes currently hang rather than error — anything behind the passport
   * `isAuthenticated` middleware, because helpers/passport.js `checkUserV3` can return without ever
   * calling done() when its DB or cache lookup fails. With no timeout the browser waits forever and
   * the UI spins with no explanation, which is exactly what the Journeys portal does today.
   *
   * 30s is well above any healthy response here (the endpoints that do work answer in under a
   * second) and turns an indefinite hang into a normal error the callers already handle.
   * Streaming is unaffected: live data goes over MQTT and the runtimes WebSocket, not axios.
   */
  if (instance.defaults.timeout === undefined || instance.defaults.timeout === 0) {
    instance.defaults.timeout = 30_000;
  }

  instance.interceptors.request.use((config) => {
    try {
      const token = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (token) config.headers['Authorization'] = `Bearer ${token}`;

      const currentNetwork = localStorage.getItem(CURRENT_NETWORK_KEY);
      if (currentNetwork) config.headers['X-NETWORK-ID'] = currentNetwork;
    } catch (error) {
      console.warn('[http] could not set auth headers', error);
    }
    return config;
  });
  return instance;
}

/**
 * Installs the headers on the GLOBAL axios instance, for code that calls bare `axios` — which is
 * how the ported Journeys portal code works (see viana-journeys-portal-26/src/axiosConfig.ts).
 * Instances created with axios.create() must call installVianaHeaders themselves.
 */
export const initializeAxiosInterceptors = () => {
  installVianaHeaders(axios);
};
